import getFileSize from '../../../../helpers/get_file_size';
import getFormatFromMime from '../../../../helpers/get_format_from_mime';

export const getDocTitle = result => {
  if (!result) return '';

  const title = result?.metadata?.settings?.attributes?.title;
  if (title) return title;

  const assemblyNumber = result?.metadata?.settings?.attributes?.acoustic_selector_attributes?.assembly_number;
  if (Array.isArray(assemblyNumber) && assemblyNumber.length > 0) {
    return assemblyNumber[0];
  }

  return result?.name;
};

export const getDocSize = result => {
  const sizeBytes =
    result?.contents?.type === 'dms' ? result.contents?.dms?.size_bytes : result?.contents?.document?.size_bytes;
  return getFileSize(sizeBytes);
};

export const getDocDescription = result => {
  if (!result) return '';

  return result.metadata?.settings?.attributes?.short_description;
};

export const getDocFilename = result => {
  if (!result) return '';

  return result?.metadata?.settings?.attributes?.acoustic_selector_attributes?.drawing_name || '';
};

export const getDocFormat = result => {
  if (!result) return '';

  let mime;
  switch (result?.contents?.type) {
    case 'dms':
      mime = result?.contents?.dms?.mime_type;
      break;
    case 'upload':
      mime = result?.contents?.document?.mime_type;
      break;
    case 'external':
      return 'external';
    default:
      mime = null;
  }

  return mime ? getFormatFromMime(mime) : '';
};

// Attributes whose value differs per result row of an assembly. When picked in
// the CMS they become columns of the assembly's results table.
export const ROW_ATTRIBUTE_KEYS = ['fire_rating', 'stc', 'iic'];

// Test numbers are shown as the references of the values they back, so they
// are never listed as attributes of their own on an assembly.
const REFERENCE_ATTRIBUTE_KEYS = ['fire_test_number', 'sound_test_number', 'iic_test_number'];

export const getAttributeKey = attribute => (attribute?.field || '').split('.').pop();

export const getAssemblyResults = result => result?.metadata?.settings?.attributes?.acoustic_selector_results || [];

export const resultCountLabel = count => `${count} ${count === 1 ? 'result' : 'results'}`;

// Filters are evaluated within one load-bearing class of an assembly, so each
// of its groups of attributes is presented to them as a target of its own: an
// assembly matches when one group meets every applied filter. A document
// without groups is filtered on its attributes as a whole.
export const getFilterTargets = result => {
  const attributes = result?.metadata?.settings?.attributes;
  const groups = attributes?.acoustic_selector_attribute_groups;

  if (!groups?.length) return [result];

  return groups.map(group => ({
    ...result,
    metadata: {
      ...result.metadata,
      settings: {
        ...result.metadata.settings,
        attributes: { ...attributes, acoustic_selector_attributes: group },
      },
    },
  }));
};

// The guide PDF holds the NBCC fire and sound tables, so it only covers an
// assembly that one of its code references lists. A reference that does not
// say whether it is a code listing is taken to be one.
export const isCoveredByGuidePdf = result => {
  const results = getAssemblyResults(result);
  if (!results.length) return true;

  return results.some(row => (row.references || []).some(reference => reference.code_reference !== false));
};

// Splits the attributes picked in the CMS into the results table columns and
// the attributes listed on the assembly card. A document without results
// keeps every attribute on the card.
export const splitAssemblyAttributes = (attributes, results) => {
  const pickedAttributes = attributes || [];

  if (!results?.length) {
    return { rowAttributes: [], cardAttributes: pickedAttributes };
  }

  const rowAttributes = pickedAttributes.filter(attribute => {
    const key = getAttributeKey(attribute);
    return ROW_ATTRIBUTE_KEYS.includes(key) && results.some(row => row[key]);
  });
  const cardAttributes = pickedAttributes.filter(attribute => {
    const key = getAttributeKey(attribute);
    return !ROW_ATTRIBUTE_KEYS.includes(key) && !REFERENCE_ATTRIBUTE_KEYS.includes(key);
  });

  return { rowAttributes, cardAttributes };
};

export const getRowReferences = (row, rowAttributeKeys) => {
  const seenNumbers = new Set();

  return (row?.references || []).filter(reference => {
    const number = reference.number.toLowerCase();
    const backsShownValue = (reference.for || []).some(key => rowAttributeKeys.includes(key));

    if (!backsShownValue || seenNumbers.has(number)) {
      return false;
    }

    seenNumbers.add(number);
    return true;
  });
};

export default {
  getDocTitle,
  getDocSize,
  getDocDescription,
  getDocFilename,
  getDocFormat,
  getAttributeKey,
  getAssemblyResults,
  resultCountLabel,
  getFilterTargets,
  isCoveredByGuidePdf,
  splitAssemblyAttributes,
  getRowReferences,
};
